<template>
  <app-page class="onl-page" fill lean>
    <!-- Aufbau wie die übrigen Tabellenseiten (Kundenwunsch 13.10.2026: „superadminseiten bitte ebenfalls umstellen“):
         leichter Titel, links die Suche statt der Filterzeile; ohne Filter und Gruppierung „Aktualisieren“ rechts in
         der Werkzeugleiste (Handy: Icon-Knopf im Kopf) -->
    <template v-if="handy" #actions>
      <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
    </template>
    <template #toolbar>
      <app-search v-model="searchText" placeholder="Benutzer, IP oder Verbindung" @input="onSearchInput" />
    </template>
    <template v-if="!handy" #toolbar-end>
      <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
    </template>
    <!-- Tabelle füllt die Höhe über der Fußleiste (Handy: natürliche Höhe); neue, geänderte und getrennte Verbindungen
         kommen wie bisher live über den Websocket „online“ -->
    <div class="app-grid-fill onl-grid" :class="{ 'onl-grid--auto': handy }">
      <dx-data-grid
        :ref="gridRefName"
        :height="gridHeight"
        :columns="columns"
        :data-source="dataSource"
        :show-borders="false"
        :show-column-lines="false"
        :show-row-lines="true"
        :column-auto-width="false"
        :allow-column-reordering="!gridTouch"
        :allow-column-resizing="!gridTouch"
        :hover-state-enabled="!gridTouch"
        :repaint-changes-only="true"
        :two-way-binding-enabled="false"
        :scrolling="scrolling"
        :element-attr="gridAttr"
        @toolbar-preparing="onToolbarPreparing"
        @content-ready="onContentReady"
      >
        <dx-pager :visible="false" />
        <dx-paging :page-size="defaultPageSize" />
        <template #userTemplate="cell">
          <span class="onl-user">{{ cell.data.value || "–" }}</span>
        </template>
        <template #timeTemplate="cell">
          <span class="onl-num">{{ timeText(cell.data.value) }}</span>
        </template>
        <template #mutedTemplate="cell">
          <span class="onl-muted">{{ cell.data.value || "–" }}</span>
        </template>
      </dx-data-grid>
    </div>
    <app-grid-footer ref="fuss" :page-sizes="pageSizes">
      <template #summary>
        <span v-if="narrowed"><strong>{{ summaryCount }}</strong> von {{ summaryLoaded }}</span>
        <span v-else><strong>{{ summaryCount }}</strong> {{ summaryNoun }}</span>
      </template>
    </app-grid-footer>
  </app-page>
</template>

<script>
import { DxDataGrid, DxPager, DxPaging } from "devextreme-vue/data-grid";
import DataGridMixin from "@/mixins/DataGridMixin";
import CustomStore from "devextreme/data/custom_store";
import axios from "axios";
import websocket from "@/aws/websocket";
import { formatDateTime, formatNumber, plural } from "@/utils/format";

export default {
  components: { DxDataGrid, DxPager, DxPaging },
  mixins: [DataGridMixin],
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Handy: AppPage fill wirkt dort nicht, das Grid wächst mit seinen Zeilen; sonst füllt es die Höhe
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    narrowed() {
      return this.summary.count !== this.summary.loaded;
    },
    summaryCount() {
      return formatNumber(this.summary.count);
    },
    summaryLoaded() {
      return formatNumber(this.summary.loaded) + " " + plural(this.summary.loaded, "Verbindung", "Verbindungen");
    },
    summaryNoun() {
      return plural(this.summary.count, "Verbindung", "Verbindungen");
    },
  },
  data() {
    return {
      gridRefName: "onlineGrid",
      searchText: "",
      gridAttr: {},
      summary: { count: 0, loaded: 0 },
      dataSource: {
        store: new CustomStore({
          key: "connection_id",
          load: async () => {
            const data = (await axios.get(`${this.$store.state.baseUrl}connection`)).data;
            const rows = Array.isArray(data) ? data : [];
            this.loadedCount = rows.length;
            return rows;
          },
        }),
        reshapeOnPush: true,
      },
      // Bildschirm: Benutzer, IP, Verbunden um, Connection-ID (gedämpft); neueste Verbindung oben
      columns: [
        { dataField: "username", caption: "Benutzer", minWidth: 160, cellTemplate: "userTemplate" },
        { dataField: "ip", caption: "IP", width: 160, cellTemplate: "mutedTemplate" },
        {
          dataField: "connection_time",
          caption: "Verbunden um",
          dataType: "datetime",
          width: 168,
          sortOrder: "desc",
          cellTemplate: "timeTemplate",
        },
        { dataField: "connection_id", caption: "Connection-ID", minWidth: 200, cellTemplate: "mutedTemplate" },
      ],
    };
  },
  created() {
    // nicht reaktiv: Anzahl der geladenen Verbindungen
    this.loadedCount = 0;
  },
  mounted() {
    websocket.subscribe("online", (message) => {
      const store = this.dataSource.store;
      if (message.data.type === "INSERT") {
        this.loadedCount += 1;
        store.push([{ type: "insert", data: message.data.data }]);
      } else if (message.data.type === "MODIFY") {
        store.push([{ type: "update", key: message.data.key.connection_id, data: message.data.data }]);
      } else if (message.data.type === "REMOVE") {
        this.loadedCount = Math.max(0, this.loadedCount - 1);
        store.push([{ type: "remove", key: message.data.key.connection_id }]);
      }
    });
  },
  destroyed() {
    websocket.unsubscribe("online");
  },
  methods: {
    grid() {
      const ref = this.$refs[this.gridRefName];
      return ref && ref.instance;
    },
    refreshDataGrid() {
      const grid = this.grid();
      if (grid) {
        grid.refresh();
      }
    },
    onSearchInput() {
      const grid = this.grid();
      if (grid) {
        grid.searchByText(this.searchText || "");
      }
    },
    onToolbarPreparing(e) {
      e.toolbarOptions.items = [];
      e.toolbarOptions.visible = false;
    },
    onContentReady(e) {
      const grid = e.component;
      const count = Math.max(Number(grid.totalCount()) || 0, 0);
      // ohne Suche alle; mit Suche „n von N“ (N = geladen, mit den Live-Änderungen)
      const loaded = this.searchText ? this.loadedCount : count;
      this.summary = { count, loaded: Math.max(loaded, count) };
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
    },
    timeText(value) {
      return value ? formatDateTime(value) : "–";
    },
  },
};
</script>

<style scoped>
/* Handy: Grid in natürlicher Höhe */
.onl-grid.onl-grid--auto {
  flex: none;
}
.onl-grid ::v-deep .onl-user {
  font-weight: 500;
  color: var(--ink);
}
.onl-grid ::v-deep .onl-num {
  font-variant-numeric: tabular-nums;
}
.onl-grid ::v-deep .onl-muted {
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
}
</style>
